import { ControlledAsyncFormCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid.tsx";
import {
	coloredBooleanColumn,
	enumColumn,
	floatColumn,
	moneyColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { SalesPartView } from "src/api/generated/erp/db/types/tables/salesPartView.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { faBoxOpenFull, faCheck } from "@fortawesome/pro-regular-svg-icons";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { SalesPartForm } from "src/components/views/erp/sales/masterData/salesParts/SalesPartForm.tsx";
import { SalesPartEditApi } from "src/api/generated/erp/sales/masterData/api/salesPartEditApi.ts";
import { RefreshableElementProps } from "src/utils/useRefreshRef.ts";
import { SalesPartApi } from "src/api/generated/erp/sales/masterData/api/salesPartApi.ts";
import { useServerSideDataGridModel } from "src/components/common/dataGrid/gridModel/useServerSideDataGridModel";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import {
	getSalesPartTypeLabel,
	getSalesPartTypeLabels,
	SalesPartType,
	SalesPartTypeValues,
} from "src/api/generated/erp/db/types/enums/salesPartType.ts";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { AavoSelectField } from "../../../../../common/inputFields/AavoSelectField.tsx";
import { SelectSiteField } from "src/components/views/erp/common/sites/SelectSiteField.tsx";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SalesPartContentLinesDataGrid } from "src/components/views/erp/sales/masterData/salesParts/SalesPartContentLinesDataGrid.tsx";
import { memo } from "react";
import { SalesPartConfigurationTemplatesDataGrid } from "src/components/views/erp/configurator/managing/salesPartConfigurationTemplates/SalesPartConfigurationTemplatesDataGrid.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { Typography } from "@mui/material";
import { renderMoneyCellWithRowCurrency } from "src/components/views/erp/sales/salesUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalesPartsDataGridBaseProps extends RefreshableElementProps {
	partId: number | null;
	showTypeFiltering?: boolean;
	showSiteFiltering?: boolean;
	showConfigurationTemplates?: boolean;
}

export const SalesPartsDataGrid = ({
	partId,
	showSiteFiltering = true,
	showTypeFiltering = true,
	showConfigurationTemplates = false,
	refreshRef,
}: SalesPartsDataGridBaseProps) => {
	const confirm = useConfirmDialog();
	const { defaultSiteId } = useGlobalInitData();
	const { openDialog } = useGenericDialog();
	const { dataGridProps, refreshData, currentParams, onlySelectedRow } = useServerSideDataGridModel({
		gridId: "9AF12557221C287D0",
		fetchData: (params) =>
			SalesPartApi.searchSalesParts({
				...params,
			}),
		refreshRef: refreshRef,
		getRowId: (row) => row.salesPartId,
		customFunctionUiActionView: "SALES_PARTS",
		initialParams: {
			partId: partId,
			searchQuery: "",
			siteId: genericNullableValue<number>(defaultSiteId),
			showInactiveSalesParts: false,
			salesPartType: genericNullableValue<SalesPartType>(),
		},
		hideDetailPanelToggle: (rows) =>
			!(showConfigurationTemplates && rows.some((row) => row.hasTemplateConfiguration)),
	});

	return (
		<ControlledAsyncFormCrudDataGrid<SalesPartView>
			disableMultipleRowSelection
			disableVirtualization={partId != null} // Should be disabled due to detail panel.
			columns={[
				textColumn({
					field: "salesPartNo",
					headerName: i18n.t("sales_part_no"),
					width: 120,
				}),
				textColumn({
					field: "salesPartDescription",
					headerName: i18n.t("description"),
					width: 200,
				}),
				textColumn({
					field: "salesPartCategoryName",
					headerName: i18n.t("sales_part_category"),
					width: 130,
				}),
				enumColumn({
					field: "salesPartType",
					headerName: i18n.t("type"),
					enumLabels: getSalesPartTypeLabels(),
				}),
				textColumn({
					field: "priceGroupName",
					headerName: i18n.t("sales_price_group"),
					width: 120,
				}),
				moneyColumn({
					field: "salesPrice",
					headerName: i18n.t("sales_price"),
					renderCell: renderMoneyCellWithRowCurrency((row) => row.currencyCode),
				}),
				textColumn({
					field: "salesPriceUnit",
					headerName: i18n.t("sales_price_unit"),
				}),
				textColumn({
					field: "salesUnit",
					headerName: i18n.t("sales_unit"),
				}),
				floatColumn({
					field: "priceFactor",
					headerName: i18n.t("price_factor"),
				}),
				floatColumn({
					field: "salesFactor",
					headerName: i18n.t("sales_factor"),
				}),
				textColumn({
					field: "vatCodeName",
					headerName: i18n.t("vat_code"),
				}),
				coloredBooleanColumn({
					field: "canBeUsedOnCustomerOrderLine",
					headerName: i18n.t("can_be_used_on_customer_order_line"),
					falseColor: "grey",
					width: 150,
				}),
				coloredBooleanColumn({
					field: "canBeUsedOnCustomerOfferLine",
					headerName: i18n.t("can_be_used_on_customer_offer_line"),
					falseColor: "grey",
					width: 150,
				}),
				coloredBooleanColumn({
					field: "hasTemplateConfiguration",
					headerName: i18n.t("has_template_configuration"),
					falseColor: "grey",
					width: 150,
				}),
				coloredBooleanColumn({
					field: "isDefault",
					headerName: i18n.t("default"),
					falseColor: "grey",
				}),
				coloredBooleanColumn({
					field: "active",
					headerName: i18n.t("active"),
					falseColor: "red",
				}),
			]}
			actionBarComponents={
				<>
					<DocumentsOfObjectButton
						objectRef={nullableAavoObjectRef("SALES_PART", onlySelectedRow?.salesPartId)}
					/>
					{partId == null && (
						<AavoTextField
							label={i18n.t("search")}
							onSubmit={async (value) => {
								await refreshData({ searchQuery: value });
							}}
						/>
					)}
					{showTypeFiltering && (
						<AavoSelectField
							label={i18n.t("type")}
							options={SalesPartTypeValues}
							getOptionKey={(o) => o}
							getOptionLabel={(o) => getSalesPartTypeLabel(o)}
							onChange={async (value) => {
								await refreshData({ salesPartType: value });
							}}
						/>
					)}
					{showSiteFiltering && (
						<SelectSiteField value={currentParams.siteId} onChange={(siteId) => refreshData({ siteId })} />
					)}
				</>
			}
			actionBarMenuComponents={[
				<MenuCheckbox
					key={"onlyActiveCheckbox"}
					label={i18n.t("show_inactive_sales_parts")}
					checked={currentParams.showInactiveSalesParts}
					onChange={async (checked) => {
						await refreshData({ showInactiveSalesParts: checked });
					}}
				/>,
			]}
			rowContextMenuComponents={({ row, onlySingleRowSelected }) => [
				<OpenObjectChangeLogButton
					key={"changeLog"}
					objectRef={{
						objectType: "SALES_PART",
						objectId: row.salesPartId,
					}}
				/>,
				onlySingleRowSelected && row.active && !row.isDefault && row.partId != null && (
					<AsyncMenuButton
						key={"setToDefault"}
						icon={faCheck}
						label={i18n.t("set_to_default")}
						onClick={async () => {
							const confirmed = await confirm({
								title: i18n.t("are_you_sure"),
								message: i18n.t("do_you_want_to_set_this_sales_part_as_default"),
							});
							if (!confirmed) return;
							await SalesPartEditApi.setToDefaultSalesPart({
								salesPartId: row.salesPartId,
							});
							await refreshData();
						}}
					/>
				),
				row.salesPartType === "PACKAGE_" && row.acquisitionMethod !== "SPLIT_BOM" && (
					<AsyncMenuButton
						key={"packageContentLines"}
						icon={faBoxOpenFull}
						label={i18n.t("package_content")}
						onClick={async () => {
							openDialog({
								title: i18n.t("package_content"),
								content: <SalesPartContentLinesDataGrid parentSalesPartId={row.salesPartId} />,
								size: "lg",
								agentContext: {
									key: defineAgentUiContextKey("erp.sales.masterData.salesPartPackageContent.salesPartsDataGrid.dialog"),
									title: i18n.t("package_content"),
									pathSegments: [i18n.t("package_content")],
									data: { parentSalesPartId: row.salesPartId },
								},
							});
						}}
					/>
				),
			]}
			remove={async ({ row }) =>
				SalesPartEditApi.delete_({
					salesPartId: row.salesPartId,
				})
			}
			form={{
				dialogSize: "md",
				dialogTitle: i18n.t("sales_part"),
				addRowEnabled: true,
				editEnabled: true,
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.sales.masterData.salesPart.formDialog"),
					data: {
						salesPartId: row?.salesPartId ?? null,
						partId: row?.partId ?? partId ?? null,
					},
					pathSegments:
						row?.salesPartNo ? [`${i18n.t("sales_part")} ${row.salesPartNo}`] : [i18n.t("sales_part")],
				}),
				component: ({ row, onCompleted, onFormEdited }) => {
					return (
						<SalesPartForm
							salesPartId={row?.salesPartId ?? null}
							partId={row?.partId ?? partId ?? null}
							onCompleted={onCompleted}
							onFormEdited={onFormEdited}
						/>
					);
				},
			}}
			getDetailPanelContent={(props) => <DetailPanelContent {...props} />}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.masterData.salesParts.grid"),
				data: { partId: partId ?? null },
				selectedRowPathSegment: (row) => `${i18n.t("sales_part")} ${row.salesPartNo}`,
				rowFields: ["salesPartId", "partId", "salesPartNo", "siteId", "salesPartPriceGroupId"],
			}}
			{...dataGridProps}
		/>
	);
};

const DetailPanelContent = memo(({ row }: { row: SalesPartView }) => {
	if (!row.hasTemplateConfiguration) return null;

	return (
		<VerticalBox
			sx={{
				marginY: 1,
				marginX: 2,
				gap: 0.5,
			}}
		>
			<Typography variant="subtitle1">{i18n.t("configuration_templates")}</Typography>
			<SalesPartConfigurationTemplatesDataGrid salesPartId={row.salesPartId} />
		</VerticalBox>
	);
});
